/**
 * Pure helpers for the Manage/Learning view-mode switch. Kept free of React and
 * JSX so they can be unit-tested in isolation (see viewMode.utils.test.ts).
 *
 * A member's *view mode* is either the management surface (`manage`) or the
 * universal learning surface (`learn`). The sidebar renders only the active
 * mode's nav group, and the avatar-menu toggle flips between the two.
 */

export type ViewMode = 'manage' | 'learn';

/** localStorage key holding the member's last-used view-mode preference. */
export const VIEW_MODE_STORAGE_KEY = 'tereiko-view-mode';

/** Landing path for the learning (consumer) surface — shared by every member. */
export const LEARN_HOME = '/learn/dashboard';

/**
 * Top-level path prefixes that belong to the management surface. A pathname
 * matches when it equals a prefix or sits directly beneath it (`/courses` and
 * `/courses/123` match `/courses`, but `/coursesXYZ` does not). `/learn/*` is
 * always the learning surface and is checked first by `surfaceOf`.
 */
export const MANAGE_PREFIXES = [
  '/dashboard',
  '/courses',
  '/learners',
  '/analytics',
  '/prizes',
  '/programs',
  '/gamification',
  '/settings',
] as const;

/** Which surface a pathname belongs to. `shared` routes (e.g. `/chat`,
 *  `/calendar`, `/`) belong to neither and fall back to the saved preference. */
export function surfaceOf(pathname: string): 'learn' | 'manage' | 'shared' {
  if (pathname === '/learn' || pathname.startsWith('/learn/')) return 'learn';
  for (const prefix of MANAGE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return 'manage';
  }
  return 'shared';
}

/** A member who cannot manage is always pinned to the learning surface. */
export function clampPreference(pref: ViewMode, canManage: boolean): ViewMode {
  return canManage ? pref : 'learn';
}

/**
 * Resolve the effective view mode for a page: the route wins (a `/learn/*` page
 * is always Learning; a management page is always Manage), and shared/root
 * routes fall back to the persisted preference — all clamped by `canManage`.
 */
export function resolveMode(args: {
  pathname: string;
  preference: ViewMode;
  canManage: boolean;
}): ViewMode {
  const surface = surfaceOf(args.pathname);
  const fromSurface =
    surface === 'learn' ? 'learn' : surface === 'manage' ? 'manage' : args.preference;
  return clampPreference(fromSurface, args.canManage);
}

/** Read the saved preference, tolerating an absent/garbled/unavailable store. */
export function readStoredPreference(): ViewMode | null {
  try {
    const value = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return value === 'manage' || value === 'learn' ? value : null;
  } catch {
    return null;
  }
}

/** Persist the preference; never throws if storage is unavailable. */
export function writeStoredPreference(mode: ViewMode): void {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    /* storage unavailable (private mode, quota) — preference is best-effort */
  }
}

/** Drop the persisted preference (called on logout so it never leaks into the
 *  next session — possibly a different member on the same browser). */
export function clearStoredPreference(): void {
  try {
    localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
